import type { CrateState, GateDef, HazardDef, LevelDef, Platform, Rect, SignDef, SwitchDef, SwitchState, WorldState } from '../../shared/types';
import { AMBER, INK, PAPER, TAU, arch, circuitMark, clamp, disc, hash, leaf, line, pill, random, rounded, spark, text } from './paint';
import { describeHazard } from './hazards';

export interface Palette {
  sky: string;
  horizon: string;
  far: string;
  middle: string;
  tree: string;
  twig: string;
  soil: string;
  soilDark: string;
  moss: string;
  mossLight: string;
  stone: string;
  water: string;
  paper: string;
  night: boolean;
}

export const PALETTES: Record<LevelDef['theme'], Palette> = {
  grove: {
    sky: '#f9f1dc', horizon: '#eee8cd', far: '#dae2cf', middle: '#c5d5c5', tree: '#b1c7b8', twig: '#8ea89a',
    soil: '#8b6d50', soilDark: '#75593f', moss: '#849c68', mossLight: '#bfd294', stone: '#b4b49b', water: '#679b94', paper: '#fff4da', night: false,
  },
  brook: {
    sky: '#f1f1de', horizon: '#e1e9da', far: '#d1dfd5', middle: '#b4ceca', tree: '#9fbfba', twig: '#7fa69f',
    soil: '#87745b', soilDark: '#6b5e4b', moss: '#81a47d', mossLight: '#c4d9a4', stone: '#b2c2b3', water: '#508e93', paper: '#f8f4df', night: false,
  },
  ruins: {
    sky: '#f5ebd8', horizon: '#e8dbc5', far: '#d8d7c7', middle: '#bdc9bb', tree: '#a5b8a7', twig: '#8d9f8c',
    soil: '#90775d', soilDark: '#715d4b', moss: '#8c9b68', mossLight: '#d0d59b', stone: '#b6ac91', water: '#648f86', paper: '#fff2db', night: false,
  },
  night: {
    sky: '#263f46', horizon: '#607a76', far: '#466465', middle: '#3e5e5d', tree: '#34534e', twig: '#6c8780',
    soil: '#685f4d', soilDark: '#514b3e', moss: '#869b77', mossLight: '#bdcb97', stone: '#969f8c', water: '#447e83', paper: '#f6ecd1', night: true,
  },
};

function tree(
  ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, fill: string, twig: string,
): void {
  ctx.save();
  ctx.translate(x, y);
  ctx.beginPath();
  ctx.moveTo(-width * 0.45, -height * 0.29);
  ctx.bezierCurveTo(-width * 0.78, -height * 0.34, -width * 0.62, -height * 0.64, -width * 0.38, -height * 0.67);
  ctx.bezierCurveTo(-width * 0.58, -height * 0.92, -width * 0.12, -height * 1.12, width * 0.08, -height * 0.96);
  ctx.bezierCurveTo(width * 0.29, -height * 1.02, width * 0.54, -height * 0.87, width * 0.4, -height * 0.7);
  ctx.bezierCurveTo(width * 0.7, -height * 0.65, width * 0.69, -height * 0.37, width * 0.37, -height * 0.32);
  ctx.bezierCurveTo(width * 0.2, -height * 0.2, -width * 0.23, -height * 0.2, -width * 0.45, -height * 0.29);
  ctx.fillStyle = fill;
  ctx.fill();
  ctx.strokeStyle = twig;
  ctx.lineWidth = width * 0.047;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(-width * 0.04, 0);
  ctx.quadraticCurveTo(width * 0.03, -height * 0.37, 0, -height * 0.72);
  ctx.moveTo(width * 0.005, -height * 0.4);
  ctx.quadraticCurveTo(-width * 0.16, -height * 0.4, -width * 0.27, -height * 0.58);
  ctx.moveTo(0, -height * 0.52);
  ctx.quadraticCurveTo(width * 0.19, -height * 0.54, width * 0.27, -height * 0.7);
  ctx.stroke();
  ctx.restore();
}

function cloud(ctx: CanvasRenderingContext2D, x: number, y: number, scale: number, alpha: number): void {
  ctx.save();
  ctx.translate(x, y);
  ctx.scale(scale, scale);
  ctx.globalAlpha = alpha;
  ctx.beginPath();
  ctx.moveTo(-60, 13);
  ctx.bezierCurveTo(-74, 7, -65, -14, -45, -10);
  ctx.bezierCurveTo(-40, -39, 1, -41, 10, -16);
  ctx.bezierCurveTo(31, -26, 52, -12, 50, 0);
  ctx.bezierCurveTo(77, -1, 86, 13, 67, 16);
  ctx.closePath();
  ctx.fillStyle = '#fffbed';
  ctx.fill();
  ctx.restore();
}

/** Cached by the renderer: no noise regeneration, per-frame gradients, or shifting scenery. */
export function drawBackdrop(ctx: CanvasRenderingContext2D, theme: LevelDef['theme'], hero = false): void {
  const p = PALETTES[theme];
  const sky = ctx.createLinearGradient(0, 0, 0, 675);
  sky.addColorStop(0, p.sky);
  sky.addColorStop(1, p.horizon);
  ctx.fillStyle = sky;
  ctx.fillRect(0, 0, 1200, 675);

  if (p.night) {
    const rng = random(74822);
    for (let i = 0; i < 62; i++) {
      const x = rng() * 1200;
      const y = 30 + rng() * 370;
      ctx.globalAlpha = 0.3 + rng() * 0.48;
      if (i % 11 === 0) spark(ctx, x, y, 3.2, '#f6e5b8');
      else disc(ctx, x, y, 0.7 + rng() * 0.6, '#e7e5ce');
    }
    ctx.globalAlpha = 1;
    disc(ctx, 994, 121, 42, '#ebe4bd');
    ctx.beginPath();
    ctx.arc(979, 109, 40, 0, TAU);
    ctx.fillStyle = sky;
    ctx.fill();
  } else {
    disc(ctx, hero ? 903 : 993, hero ? 164 : 131, hero ? 65 : 46, '#f4df9f');
    ctx.beginPath();
    ctx.arc(hero ? 903 : 993, hero ? 164 : 131, hero ? 77 : 57, 0, TAU);
    ctx.strokeStyle = '#dec99a';
    ctx.globalAlpha = 0.42;
    ctx.lineWidth = 1;
    ctx.stroke();
    ctx.globalAlpha = 1;
    cloud(ctx, 281, 142, 1.2, 0.72);
    cloud(ctx, 774, 103, 0.8, 0.55);
    cloud(ctx, 1068, 265, 1.3, 0.42);
    // A pair of small, quiet swifts, drawn rather than sourced.
    for (const [x, y, s] of [[526, 135, 1], [551, 151, 0.65]]) {
      ctx.beginPath();
      ctx.moveTo(x - 7 * s, y + 2 * s);
      ctx.quadraticCurveTo(x - 3 * s, y - 2 * s, x, y + 2 * s);
      ctx.quadraticCurveTo(x + 3 * s, y - 2 * s, x + 7 * s, y + 2 * s);
      ctx.strokeStyle = p.twig;
      ctx.lineWidth = 1.4;
      ctx.stroke();
    }
  }

  // Broad low-contrast silhouettes are deliberately different from collision-edged terrain.
  ctx.beginPath();
  ctx.moveTo(0, 390);
  ctx.bezierCurveTo(180, 299, 250, 455, 430, 364);
  ctx.bezierCurveTo(587, 285, 722, 394, 827, 336);
  ctx.bezierCurveTo(1020, 242, 1101, 343, 1200, 291);
  ctx.lineTo(1200, 675);
  ctx.lineTo(0, 675);
  ctx.fillStyle = p.far;
  ctx.fill();

  const distant = [[29, 484, 80, 284], [152, 460, 70, 202], [329, 493, 94, 205], [491, 466, 68, 235], [704, 467, 92, 263], [908, 456, 62, 233], [1150, 473, 113, 298]];
  for (const [x, y, w, h] of distant) tree(ctx, x, y, w, h, p.middle, p.middle);

  if (theme === 'ruins') {
    ctx.fillStyle = p.middle;
    for (const [x, y, w, h] of [[138, 267, 106, 244], [276, 337, 86, 185], [844, 247, 137, 266]]) {
      arch(ctx, x, y, w, h);
      ctx.fill();
      ctx.save();
      ctx.globalCompositeOperation = 'source-over';
      arch(ctx, x + 23, y + 37, w - 46, h - 37);
      ctx.fillStyle = p.far;
      ctx.fill();
      ctx.restore();
      ctx.fillStyle = p.middle;
    }
    ctx.fillRect(824, 480, 195, 23);
    line(ctx, [985, 500, 980, 379, 1009, 379, 1012, 500], p.middle, 3);
  }

  ctx.beginPath();
  ctx.moveTo(0, 517);
  ctx.bezierCurveTo(174, 451, 285, 524, 415, 493);
  ctx.bezierCurveTo(563, 459, 663, 548, 823, 474);
  ctx.bezierCurveTo(995, 393, 1070, 486, 1200, 449);
  ctx.lineTo(1200, 675);
  ctx.lineTo(0, 675);
  ctx.fillStyle = p.middle;
  ctx.fill();

  tree(ctx, -14, 590, 181, 490, p.tree, p.twig);
  tree(ctx, 1193, 552, 154, 397, p.tree, p.twig);
  if (hero) tree(ctx, 1029, 502, 100, 278, p.tree, p.twig);

  if (theme === 'brook') {
    ctx.beginPath();
    ctx.moveTo(985, 515);
    ctx.bezierCurveTo(793, 527, 1037, 579, 697, 626);
    ctx.bezierCurveTo(572, 647, 544, 652, 443, 675);
    ctx.lineTo(725, 675);
    ctx.bezierCurveTo(1200, 556, 860, 563, 1087, 515);
    ctx.fillStyle = '#c3ddd6';
    ctx.fill();
  }

  // Delicate fern and seed shapes sit near the edges, away from the useful airspace.
  for (const [x, y, s] of [[32, 588, 1], [101, 630, 0.7], [1126, 570, 0.85], [1179, 620, 1.2]]) {
    for (let i = 0; i < 5; i++) leaf(ctx, x, y, (27 + i * 4) * s, (i - 2) * 0.43, p.twig);
  }

  const grain = random(20014);
  ctx.fillStyle = p.night ? '#f8f1d8' : '#5b6349';
  ctx.globalAlpha = p.night ? 0.026 : 0.038;
  for (let i = 0; i < 2100; i++) {
    const x = grain() * 1200;
    const y = grain() * 675;
    ctx.fillRect(x, y, grain() > 0.88 ? 1.8 : 0.8, 0.8);
  }
  ctx.globalAlpha = 1;
  if (!hero) {
    // A quiet wash at the playable elevations keeps forest detail behind contacts.
    // It is part of the cached backdrop, so it never chases or flickers around actors.
    const quiet = ctx.createLinearGradient(0, 130, 0, 620);
    quiet.addColorStop(0, p.night ? 'rgba(38,63,70,0)' : 'rgba(249,241,220,0)');
    quiet.addColorStop(0.5, p.night ? 'rgba(38,63,70,.34)' : 'rgba(249,241,220,.30)');
    quiet.addColorStop(1, p.night ? 'rgba(38,63,70,.46)' : 'rgba(249,241,220,.57)');
    ctx.fillStyle = quiet;
    ctx.fillRect(0, 130, 1200, 545);
  }
}

export function drawPlatform(ctx: CanvasRenderingContext2D, platform: Platform, p: Palette): void {
  const { x, y, w, h } = platform;
  if (w <= 0 || h <= 0) return;
  const wood = platform.kind === 'wood' || platform.kind === 'bridge';
  const stone = platform.kind === 'stone';
  const rng = random(hash(platform.id));
  ctx.save();
  ctx.lineJoin = 'round';
  // All visual mass begins at the actual collision top, including moving surfaces.
  rounded(ctx, x, y, w, h, Math.min(4, h / 4));
  ctx.fillStyle = wood ? '#a17d50' : stone ? p.stone : p.soil;
  ctx.fill();
  ctx.save();
  ctx.clip();
  ctx.fillStyle = wood ? '#7a6042' : p.soilDark;
  ctx.globalAlpha = wood ? 0.5 : 0.25;
  ctx.fillRect(x, y + h - Math.min(9, h / 3), w, Math.min(9, h / 3));
  ctx.globalAlpha = 1;

  if (wood) {
    for (let bx = x + 2; bx < x + w; bx += 23) {
      line(ctx, [bx, y + 3, bx, y + h - 2], '#69583f', 1.5);
      line(ctx, [bx + 4, y + 6, Math.min(bx + 18, x + w - 2), y + 6], '#c8ab74', 1.2);
      if (h > 15) disc(ctx, bx + 10, y + h - 5, 1.25, '#594b39');
    }
  } else if (stone) {
    const rows = Math.max(1, Math.floor(h / 25));
    for (let row = 0; row < rows; row++) {
      const sy = y + (row + 1) * 25;
      line(ctx, [x + 2, sy, x + w - 2, sy], '#8d947d', 1);
      for (let sx = x + (row % 2 ? 23 : 43); sx < x + w; sx += 55) {
        line(ctx, [sx, sy - 24, sx + 2, sy - 10, sx, sy], '#8d947d', 1);
      }
    }
  } else {
    ctx.strokeStyle = p.soilDark;
    ctx.lineWidth = 1;
    for (let dy = 24; dy < h; dy += 29) {
      ctx.beginPath();
      ctx.moveTo(x, y + dy);
      ctx.bezierCurveTo(x + w * 0.3, y + dy - 6, x + w * 0.66, y + dy + 7, x + w, y + dy - 3);
      ctx.globalAlpha = 0.28;
      ctx.stroke();
    }
    ctx.globalAlpha = 0.34;
    for (let i = 0; i < Math.min(140, w * h / 800); i++) {
      const px = x + rng() * w;
      const py = y + 12 + rng() * Math.max(1, h - 14);
      rounded(ctx, px, py, 2 + rng() * 4, 1.6, 1);
      ctx.fillStyle = i % 4 === 0 ? '#cfb787' : p.soilDark;
      ctx.fill();
    }
    ctx.globalAlpha = 1;
  }

  if (!wood) {
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x + w, y);
    ctx.lineTo(x + w, y + 7);
    for (let tx = x + w; tx >= x; tx -= 15) {
      ctx.lineTo(tx - 7, y + 8 + rng() * 3.8);
      ctx.lineTo(tx - 15, y + 6 + rng() * 3);
    }
    ctx.closePath();
    ctx.fillStyle = p.moss;
    ctx.fill();
    line(ctx, [x + 2, y + 2.5, x + w - 2, y + 2.5], p.mossLight, 2);
    for (let tx = x + 10; tx < x + w - 4; tx += 32 + rng() * 24) {
      line(ctx, [tx, y + 5, tx + 4, y + 5], p.mossLight, 1);
    }
  } else {
    line(ctx, [x + 1, y + 2.5, x + w - 1, y + 2.5], '#e0c58e', 2);
  }
  ctx.restore();

  rounded(ctx, x + 0.8, y + 0.8, w - 1.6, Math.max(1, h - 1.6), Math.min(3, h / 4));
  ctx.strokeStyle = INK;
  ctx.lineWidth = 1.7;
  ctx.stroke();
  // A single dark, straight edge makes the contact surface especially easy to trace.
  line(ctx, [x + 2, y + 0.8, x + w - 2, y + 0.8], INK, 2.1);
  if (platform.switchIds?.length) line(ctx, [x + 5, y + 3.7, x + w - 5, y + 3.7], '#f0c366', 2);
  if (platform.kind === 'bridge') {
    for (const bx of [x + 6, x + w - 10]) {
      rounded(ctx, bx, y + 1, 5, Math.min(15, h - 1), 1);
      ctx.fillStyle = '#596b58';
      ctx.fill();
      disc(ctx, bx + 2.5, y + 5, 1, '#d6cfac');
    }
  }
  ctx.restore();
}

export function drawDormantBridge(ctx: CanvasRenderingContext2D, platform: Platform): void {
  if (platform.motion) return;
  ctx.save();
  ctx.globalAlpha = 0.6;
  ctx.setLineDash([3, 7]);
  ctx.strokeStyle = '#857653';
  ctx.lineWidth = 1.3;
  rounded(ctx, platform.x + 1, platform.y + 1, platform.w - 2, Math.max(5, platform.h - 2), 3);
  ctx.stroke();
  ctx.setLineDash([]);
  for (const x of [platform.x, platform.x + platform.w]) {
    line(ctx, [x, platform.y - 3, x, platform.y + 5], INK, 2);
  }
  ctx.restore();
}

export function drawCrate(ctx: CanvasRenderingContext2D, crate: CrateState): void {
  const { x, y, w, h } = crate;
  ctx.save();
  rounded(ctx, x + 0.8, y + 0.8, w - 1.6, h - 1.6, 3);
  ctx.fillStyle = '#b28b56';
  ctx.fill();
  ctx.strokeStyle = INK;
  ctx.lineWidth = 2;
  ctx.stroke();
  for (let yy = y + 9; yy < y + h - 4; yy += 10) {
    line(ctx, [x + 4, yy, x + w - 4, yy], '#866d49', 1.25);
    line(ctx, [x + 8, yy - 3, x + w * 0.58, yy - 3], '#c5a36a', 1);
  }
  rounded(ctx, x + 4, y + 4, w - 8, h - 8, 1.5);
  ctx.strokeStyle = '#dfbd7e';
  ctx.lineWidth = 4;
  ctx.stroke();
  // Recognisable structural X bracing, including the darker cut edge.
  line(ctx, [x + 6, y + h - 6, x + w - 6, y + 6], '#725d40', 7);
  line(ctx, [x + 6, y + 6, x + w - 6, y + h - 6], '#725d40', 7);
  line(ctx, [x + 6, y + h - 7, x + w - 6, y + 5], '#d4b171', 4.5);
  line(ctx, [x + 6, y + 5, x + w - 6, y + h - 7], '#dfbd7e', 4.5);
  for (const [px, py] of [[x + 6, y + 6], [x + w - 6, y + 6], [x + 6, y + h - 6], [x + w - 6, y + h - 6]]) {
    disc(ctx, px, py, 1.45, INK);
  }
  disc(ctx, x + w - 9, y + h - 9, 6.5, INK);
  text(ctx, String(crate.weight ?? 2), x + w - 9, y + h - 8.6, 9, PAPER, 'center', 900);
  ctx.restore();
}

export interface CircuitDestination { targetId: string; kind: 'gate' | 'platform' | 'hazard'; x: number; y: number }

/** The lettered circuit ends of one plate: where its dotted link arrives under each gate,
 * switched platform and (unless the beam is currently held by that plate) hazard. */
export function circuitDestinations(level: LevelDef, world: WorldState, plate: SwitchDef, includeHeld = false): CircuitDestination[] {
  return [
    ...level.gates.filter(g => g.switchIds.includes(plate.id)).map(g => ({
      targetId: g.id, kind: 'gate' as const,
      x: g.x + g.w / 2 + (g.switchIds.indexOf(plate.id) - (g.switchIds.length - 1) / 2) * 18, y: g.y + g.h + 6,
    })),
    ...level.platforms.filter(p => p.switchIds?.includes(plate.id)).map(p => ({
      targetId: p.id, kind: 'platform' as const,
      x: p.x + p.w / 2 + (p.switchIds!.indexOf(plate.id) - (p.switchIds!.length - 1) / 2) * 18, y: p.y + p.h + 5,
    })),
    ...level.hazards.filter(h => h.disabledBy?.includes(plate.id) && (includeHeld || !(h.kind === 'beam' && describeHazard(h, level, world).held))).map(h => ({
      targetId: h.id, kind: 'hazard' as const,
      x: h.x + h.w / 2 + (h.disabledBy!.indexOf(plate.id) - (h.disabledBy!.length - 1) / 2) * 18, y: h.y + h.h + 5,
    })),
  ];
}

/** The dotted link's polyline (world px) from a plate's underside to a circuit end. The same
 * points draw the link and carry the cooperative pulse, so the two can never disagree. */
export function circuitLinkPath(plate: SwitchDef, plateIndex: number, destination: { x: number; y: number }): { x: number; y: number }[] {
  const sx = plate.x + plate.w / 2;
  const sy = plate.y + plate.h + 7;
  const bendY = Math.min(646, Math.max(sy + 26, destination.y + 26) + (plateIndex % 3) * 10);
  const dir = Math.sign(destination.x - sx);
  return [{ x: sx, y: sy }, { x: sx, y: bendY - 6 }, { x: sx + dir * 6, y: bendY }, { x: destination.x - dir * 6, y: bendY }, { x: destination.x, y: bendY - 6 }, { x: destination.x, y: destination.y }];
}

export function drawCircuits(
  ctx: CanvasRenderingContext2D, level: LevelDef, world: WorldState, time: number, reducedMotion: boolean,
  showMarks = true,
): void {
  ctx.save();
  ctx.lineCap = 'round';
  for (let index = 0; index < level.switches.length; index++) {
    const plate = level.switches[index];
    const active = world.switches.find(s => s.id === plate.id)?.active ?? false;
    for (const destination of circuitDestinations(level, world, plate)) {
      const [start, drop, bendIn, bendOut, rise, end] = circuitLinkPath(plate, index, destination);
      const bendY = bendIn.y;
      ctx.beginPath();
      ctx.moveTo(start.x, start.y);
      ctx.lineTo(drop.x, drop.y);
      ctx.quadraticCurveTo(start.x, bendY, bendIn.x, bendIn.y);
      ctx.lineTo(bendOut.x, bendOut.y);
      ctx.quadraticCurveTo(end.x, bendY, rise.x, rise.y);
      ctx.lineTo(end.x, end.y);
      ctx.setLineDash([1, 8]);
      ctx.lineDashOffset = active && !reducedMotion ? -time * 13 : 0;
      ctx.strokeStyle = active ? '#f4cc70' : '#c3b894';
      ctx.globalAlpha = active ? 1 : 0.4;
      ctx.lineWidth = active ? 4 : 1.8;
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.globalAlpha = 1;
      if (showMarks) circuitMark(ctx, end.x, end.y + 1, index, active, 7.5);
    }
  }
  ctx.restore();
}

export function drawSwitch(
  ctx: CanvasRenderingContext2D, plate: SwitchDef, state: SwitchState | undefined, index: number, p: Palette,
  showLabel = true,
): void {
  const active = state?.active ?? false;
  const depressed = (state?.count ?? 0) > 0;
  const { x, y, w, h } = plate;
  ctx.save();
  rounded(ctx, x, y + Math.max(2, h - 7), w, 7, 3);
  ctx.fillStyle = INK;
  ctx.fill();
  const top = y + (depressed ? Math.min(3, h / 3) : 0);
  rounded(ctx, x + 2, top, w - 4, Math.max(4, h - 4 - (depressed ? 2 : 0)), 3);
  ctx.fillStyle = active ? '#ebbf59' : '#e1d6a7';
  ctx.fill();
  ctx.strokeStyle = INK;
  ctx.lineWidth = 1.8;
  ctx.stroke();
  line(ctx, [x + 8, top + 2.3, x + w - 8, top + 2.3], active ? '#fff0a5' : '#faf0d0', 1.5);
  for (let sx = x + 14; sx < x + w - 8; sx += 12) disc(ctx, sx, top + 6, 1, active ? '#755b2e' : '#9b9476');

  if (!showLabel) { ctx.restore(); return; }

  const labelY = Math.min(652, y + h + 18);
  const count = `${state?.count ?? 0} / ${state?.required ?? plate.required}`;
  const width = pill(ctx, count, x + w / 2 + 10, labelY, {
    fill: active ? '#e9bf62' : p.paper, color: INK, stroke: INK, size: 13, height: 25, padding: 21,
  });
  circuitMark(ctx, x + w / 2 - width / 2 - 1, labelY, index, active, 10);
  if (plate.mode === 'latch') {
    const lx = x + w / 2 + width / 2 + 20;
    ctx.beginPath();
    ctx.arc(lx, labelY, 5.5, 0, TAU);
    ctx.strokeStyle = active ? '#f6d276' : p.paper;
    ctx.lineWidth = 2;
    ctx.stroke();
    if (state?.latched) line(ctx, [lx - 2.5, labelY, lx - 0.5, labelY + 2, lx + 3, labelY - 2.6], INK, 1.8);
    else line(ctx, [lx, labelY - 3, lx, labelY], p.paper, 1.2);
  } else if ((state?.graceLeft ?? 0) > 0 && !depressed) {
    ctx.beginPath();
    ctx.arc(x + w - 4, y - 5, 5, -Math.PI / 2, -Math.PI / 2 + TAU * clamp((state?.graceLeft ?? 0) / (plate.grace || 1), 0, 1));
    ctx.strokeStyle = AMBER;
    ctx.lineWidth = 2.5;
    ctx.stroke();
  }
  ctx.restore();
}

function drawClosedGateLeaf(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, p: Palette): void {
  rounded(ctx, x + 1, y + 1, w - 2, h - 2, 3);
  ctx.fillStyle = p.night ? '#425b50' : '#b9bea0';
  ctx.fill();
  ctx.strokeStyle = INK;
  ctx.lineWidth = 2;
  ctx.stroke();
  for (let bx = x + 5; bx < x + w - 2; bx += 10) {
    rounded(ctx, bx, y + 3, 4.5, h - 6, 2);
    ctx.fillStyle = '#695f46';
    ctx.fill();
    line(ctx, [bx + 1.3, y + 5, bx + 1.3, y + h - 5], '#ada078', 1);
  }
  for (const yy of [y + 6, y + h - 11]) {
    rounded(ctx, x - 1, yy, w + 2, 6, 2);
    ctx.fillStyle = '#9d8153';
    ctx.fill();
    ctx.strokeStyle = INK;
    ctx.lineWidth = 1.5;
    ctx.stroke();
  }
  const cy = y + Math.min(51, h / 2);
  disc(ctx, x + w / 2, cy, Math.min(11, w / 2 + 2), '#edcc84');
  ctx.strokeStyle = INK;
  ctx.lineWidth = 1.5;
  ctx.stroke();
  disc(ctx, x + w / 2, cy - 1.5, 2.3, INK);
  line(ctx, [x + w / 2, cy, x + w / 2, cy + 4.5], INK, 2);
}

function drawOpenGateHeader(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, settled: number): void {
  rounded(ctx, x - 2, y, w + 4, 7, 2);
  ctx.fillStyle = '#879873';
  ctx.fill();
  ctx.strokeStyle = INK;
  ctx.lineWidth = 1.6;
  ctx.stroke();
  if (settled <= 0) return;
  ctx.globalAlpha = settled;
  for (const offset of [8, 13, 18]) {
    rounded(ctx, x + 2, y + offset, Math.max(4, w - 4), 4, 1.5);
    ctx.fillStyle = offset === 8 ? '#d4bb7d' : '#8e9670';
    ctx.fill();
    ctx.strokeStyle = INK; ctx.lineWidth = 1; ctx.stroke();
  }
  disc(ctx, x + w / 2, y + 31, 8, '#d8deb0');
  line(ctx, [x + w / 2 - 3, y + 31, x + w / 2 - 0.5, y + 33.3, x + w / 2 + 4, y + 28.5], INK, 1.8);
  ctx.globalAlpha = 1;
}

/**
 * `opening` is a 0..1 presentation progress for a gate the authority has ALREADY
 * reported open: the closed leaf slides up into its lintel over that fraction. It
 * never opens a gate the received world says is shut, and 1 is the settled open art.
 */
export function drawGate(ctx: CanvasRenderingContext2D, gate: GateDef, open: boolean, p: Palette, opening = 1): void {
  const { x, y, w, h } = gate;
  const progress = open ? clamp(Number.isFinite(opening) ? opening : 1, 0, 1) : 0;
  ctx.save();
  if (!open) {
    drawClosedGateLeaf(ctx, x, y, w, h, p);
  } else if (progress >= 1) {
    ctx.globalAlpha = 0.22;
    ctx.setLineDash([2, 7]);
    ctx.strokeStyle = p.night ? '#c1ce9d' : '#6d876a';
    ctx.lineWidth = 1.6;
    ctx.strokeRect(x + 1, y + 1, w - 2, h - 2);
    ctx.setLineDash([]);
    ctx.globalAlpha = 1;
    drawOpenGateHeader(ctx, x, y, w, 1);
  } else {
    // Mid-rise: the ghost outline fades in while the leaf rises out of the cut.
    ctx.globalAlpha = 0.22 * progress;
    ctx.setLineDash([2, 7]);
    ctx.strokeStyle = p.night ? '#c1ce9d' : '#6d876a';
    ctx.lineWidth = 1.6;
    ctx.strokeRect(x + 1, y + 1, w - 2, h - 2);
    ctx.setLineDash([]);
    ctx.globalAlpha = 1;
    ctx.save();
    ctx.beginPath();
    ctx.rect(x - 3, y, w + 6, h + 2);
    ctx.clip();
    const rise = progress * progress * (3 - 2 * progress);
    ctx.translate(0, -rise * h);
    drawClosedGateLeaf(ctx, x, y, w, h, p);
    ctx.restore();
    drawOpenGateHeader(ctx, x, y, w, clamp((progress - 0.55) / 0.45, 0, 1));
  }
  ctx.restore();
}

export function drawKey(ctx: CanvasRenderingContext2D, x: number, y: number, scale = 1, angle = -0.27): void {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(angle);
  ctx.scale(scale, scale);
  // A ring, shank and two teeth: unmistakably a key even without its sparkles.
  ctx.beginPath();
  ctx.arc(0, -5.5, 7.5, 0, TAU);
  ctx.fillStyle = '#f2c461';
  ctx.fill();
  ctx.strokeStyle = '#695132';
  ctx.lineWidth = 2;
  ctx.stroke();
  rounded(ctx, -2.3, 0, 4.6, 15, 1);
  ctx.fillStyle = '#efbc52';
  ctx.fill();
  ctx.stroke();
  line(ctx, [1, 8.5, 6, 8.5, 6, 11], '#695132', 4.6);
  line(ctx, [1, 8.5, 6, 8.5, 6, 11], '#efbc52', 2.1);
  disc(ctx, 0, -5.5, 3.25, '#fff5d7');
  line(ctx, [-4, -9, -1, -11], '#fff2c3', 1.5);
  ctx.restore();
}

export function drawDoor(
  ctx: CanvasRenderingContext2D, door: Rect,
  options: { unlocked: boolean; waiting: number; total: number; enteredColors: string[]; palette: Palette; time: number; reducedMotion: boolean; hero?: boolean; showLabel?: boolean },
): void {
  const { x, y, w, h } = door;
  const { palette: p, unlocked } = options;
  ctx.save();
  const archWidth = w;
  arch(ctx, x, y, archWidth, h);
  ctx.fillStyle = '#9f8358';
  ctx.fill();
  ctx.strokeStyle = INK;
  ctx.lineWidth = 3;
  ctx.stroke();
  arch(ctx, x + 6, y + 7, w - 12, h - 8);
  ctx.fillStyle = unlocked ? '#edc77b' : '#526354';
  ctx.fill();
  ctx.strokeStyle = '#68583d';
  ctx.lineWidth = 2;
  ctx.stroke();

  if (unlocked) {
    ctx.save();
    arch(ctx, x + 8, y + 9, w - 16, h - 10);
    ctx.clip();
    const light = ctx.createLinearGradient(0, y + 10, 0, y + h);
    light.addColorStop(0, '#fff3c7');
    light.addColorStop(1, '#d9a956');
    ctx.fillStyle = light;
    ctx.fillRect(x + 8, y + 9, w - 16, h - 10);
    // Interior floorboards suggest a warm room rather than an enormous bloom.
    for (let i = 0; i < 5; i++) line(ctx, [x + w / 2, y + h * 0.55, x + 4 + i * (w - 8) / 4, y + h], '#c69550', 0.9);
    ctx.restore();
    spark(ctx, x + w / 2, y + h * 0.43, 10, '#fff6d7');
    if (!options.reducedMotion) {
      for (let i = 0; i < 3; i++) {
        const rise = (options.time * 0.21 + i / 3) % 1;
        ctx.globalAlpha = Math.sin(rise * Math.PI) * 0.65;
        disc(ctx, x + w * (0.33 + i * 0.15), y + h * (0.83 - rise * 0.49), 1.4, '#fff9d7');
      }
      ctx.globalAlpha = 1;
    }
  } else {
    ctx.save();
    arch(ctx, x + 8, y + 9, w - 16, h - 10);
    ctx.clip();
    for (let xx = x + 12; xx < x + w - 9; xx += 10) line(ctx, [xx, y + 6, xx, y + h - 3], '#435549', 1.3);
    ctx.restore();
    disc(ctx, x + w / 2, y + h * 0.46, 10, '#edd292');
    drawKey(ctx, x + w / 2, y + h * 0.46, 0.52, 0);
    disc(ctx, x + w - 17, y + h * 0.69, 2.7, '#d2ae64');
  }

  // Chunky joints and just a few leaves frame the actual exit volume.
  for (const [px, py] of [[x + 3, y + h - 19], [x + w - 3, y + h - 19], [x + 6, y + h * 0.45], [x + w - 6, y + h * 0.45]]) {
    line(ctx, [px - 2, py, px + 2, py], '#5e5841', 1.5);
  }
  leaf(ctx, x + 10, y + 11, 16, -0.75, p.moss);
  leaf(ctx, x + 12, y + 10, 12, 0.06, p.mossLight);
  leaf(ctx, x + w - 7, y + 22, 13, 0.7, p.moss);
  rounded(ctx, x - 4, y + h - 3, w + 8, 6, 2);
  ctx.fillStyle = '#c4b28b';
  ctx.fill();
  ctx.strokeStyle = INK;
  ctx.lineWidth = 1.8;
  ctx.stroke();

  if (!options.hero && options.showLabel !== false) {
    const label = options.waiting === 0 ? 'ALL HOME' : `${options.waiting} TO GO`;
    pill(ctx, label, x + w / 2, y - 25, { fill: p.paper, stroke: INK, size: 12, height: 25 });
    const total = Math.max(0, Math.min(8, options.total));
    const spacing = 9;
    for (let i = 0; i < total; i++) {
      disc(ctx, x + w / 2 + (i - (total - 1) / 2) * spacing, y - 6, 2.8, options.enteredColors[i] ?? '#e8dfc2');
      ctx.strokeStyle = INK;
      ctx.lineWidth = 1;
      ctx.stroke();
    }
  }
  ctx.restore();
}

export function drawHazard(
  ctx: CanvasRenderingContext2D, hazard: HazardDef, active: boolean, warning: boolean,
  p: Palette, time: number, reducedMotion: boolean, cycleProgress: number,
  protectedBySwitch = false,
  showLabel = true,
  keyLocked = false,
): void {
  const { x, y, w, h } = hazard;
  const pulse = reducedMotion ? 0.5 : (Math.sin(time * (warning ? 10 : 4)) + 1) / 2;
  ctx.save();
  ctx.lineJoin = 'round';
  if (hazard.kind === 'water') {
    ctx.fillStyle = active ? p.water : '#93b8a4';
    ctx.fillRect(x, y, w, h);
    ctx.save();
    ctx.beginPath();
    ctx.rect(x, y, w, h);
    ctx.clip();
    for (let row = 0; row < Math.min(8, h / 9); row++) {
      ctx.beginPath();
      for (let xx = x - 2; xx <= x + w + 3; xx += 4) {
        const yy = y + 5 + row * 11 + Math.sin(xx * 0.075 + (reducedMotion ? 0 : time * 1.8) + row) * 1.7;
        if (xx === x - 2) ctx.moveTo(xx, yy); else ctx.lineTo(xx, yy);
      }
      ctx.strokeStyle = row % 2 ? 'rgba(222,244,218,.26)' : 'rgba(35,74,76,.28)';
      ctx.lineWidth = row === 0 ? 2 : 1;
      ctx.stroke();
    }
    ctx.restore();
    line(ctx, [x, y + 1, x + w, y + 1], '#335e5d', 2.5);
    for (let xx = x + 30; xx < x + w - 8; xx += 86) {
      line(ctx, [xx - 4, y + 18, xx, y + 14, xx + 4, y + 18, xx + 8, y + 14], '#d0ded0', 1.5);
    }
  } else if (hazard.kind === 'bramble') {
    ctx.fillStyle = active ? 'rgba(129,78,66,.19)' : 'rgba(124,143,100,.1)';
    ctx.fillRect(x, y, w, h);
    const thornHeight = active ? h - 2 : Math.min(5, h * 0.25);
    for (let xx = x + 1; xx < x + w - 1; xx += 13) {
      ctx.beginPath();
      ctx.moveTo(xx, y + h - 1);
      ctx.lineTo(Math.min(xx + 5.5, x + w - 1), y + h - thornHeight);
      ctx.lineTo(Math.min(xx + 12, x + w - 1), y + h - 1);
      ctx.fillStyle = active ? '#8c6553' : '#7f9474';
      ctx.fill();
      ctx.strokeStyle = active ? '#493f36' : '#5a735c';
      ctx.lineWidth = 1.6;
      ctx.stroke();
    }
    line(ctx, [x, y + h - 1, x + w, y + h - 1], INK, 2);
    if (active) {
      ctx.setLineDash([2, 5]);
      line(ctx, [x, y, x + w, y], '#8e5e4b', 1.3);
      ctx.setLineDash([]);
    }
  } else {
    ctx.save();
    ctx.beginPath();
    ctx.rect(x, y, w, h);
    ctx.clip();
    ctx.fillStyle = active ? `rgba(216,135,90,${0.13 + pulse * 0.025})` : warning ? 'rgba(236,184,77,.16)' : 'rgba(94,141,119,.05)';
    ctx.fillRect(x, y, w, h);
    if (warning) {
      for (let xx = x - h; xx < x + w + h; xx += 17) {
        line(ctx, [xx, y + h, xx + h, y], 'rgba(165,125,47,.22)', 2);
      }
    }
    if (active) {
      // Distinct energy threads make the protected roof's interception legible.
      for (let px = x + 15; px < x + w - 5; px += 27) {
        ctx.beginPath(); ctx.moveTo(px, y);
        for (let py = y + 8; py <= y + h + 8; py += 8) ctx.lineTo(px + Math.sin(py * 0.055 + (reducedMotion ? 0 : time * 2)) * 1.5, py);
        ctx.strokeStyle = '#bd7953'; ctx.lineWidth = 3.2; ctx.stroke();
        ctx.strokeStyle = '#f6d397'; ctx.lineWidth = 1.1; ctx.stroke();
      }
    }
    ctx.restore();
    ctx.setLineDash(active ? [] : [3, 6]);
    ctx.strokeStyle = active ? p.night ? '#d69a70' : '#925e49' : warning ? '#b0873d' : '#789581';
    ctx.lineWidth = active ? 2 : 1.5;
    ctx.strokeRect(x + 0.8, y + 0.8, w - 1.6, h - 1.6);
    ctx.setLineDash([]);
    const ends = h > w ? [[x + w / 2, y], [x + w / 2, y + h]] : [[x, y + h / 2], [x + w, y + h / 2]];
    for (const [ex, ey] of ends) {
      disc(ctx, ex, ey, 5.5, INK);
      disc(ctx, ex, ey, 2.5, active ? '#e9a374' : warning ? AMBER : '#b5d4a1');
    }
  }

  if (hazard.cycle && showLabel) {
    const cx = x + w / 2;
    const cy = Math.max(23, y - 17);
    const label = protectedBySwitch ? 'HELD' : keyLocked ? 'HOLD A' : active ? 'WAIT' : warning ? 'SOON' : 'GO';
    const width = pill(ctx, label, cx, cy, {
      fill: active ? '#f1d4b6' : warning ? '#f0d58f' : '#d9e6c0', color: INK, stroke: INK, size: 10.5, height: 22, padding: 19,
    });
    if (protectedBySwitch) {
      line(ctx, [cx + width / 2 + 6, cy, cx + width / 2 + 9, cy + 3, cx + width / 2 + 15, cy - 4], '#bcd8a9', 2.5);
    } else if (!keyLocked) {
      ctx.beginPath();
      ctx.arc(cx + width / 2 + 10, cy, 5.5, -Math.PI / 2, -Math.PI / 2 + TAU * cycleProgress);
      ctx.strokeStyle = active ? '#835940' : '#658263';
      ctx.lineWidth = 2;
      ctx.stroke();
    }
  } else if (!hazard.cycle && hazard.kind !== 'water' && w > 55) {
    const cx = x + w / 2;
    disc(ctx, cx, y + h - 8, 5.5, '#f0d6ae');
    text(ctx, '!', cx, y + h - 7.8, 9, INK, 'center', 900);
  }
  ctx.restore();
}

export function drawSigns(ctx: CanvasRenderingContext2D, signs: SignDef[], p: Palette, avoid: Rect[] = []): void {
  for (const sign of signs) {
    ctx.save();
    ctx.font = '600 11px "Trebuchet MS", system-ui, sans-serif';
    const words = sign.text.split(/\s+/);
    const rows: string[] = [];
    let row = '';
    for (const word of words) {
      const candidate = row ? `${row} ${word}` : word;
      if (ctx.measureText(candidate).width > 190 && row) { rows.push(row); row = word; }
      else row = candidate;
    }
    if (row) rows.push(row);
    const shown = rows.slice(0, 3);
    const width = Math.max(55, ...shown.map(r => ctx.measureText(r).width)) + 22;
    const height = shown.length * 14 + 14;
    let x = clamp(sign.x, width / 2 + 8, 1192 - width / 2);
    let y = clamp(sign.y, height / 2 + 8, 640 - height / 2);
    // Never place an instruction card across a damaging edge or a collectible.
    for (const obstacle of avoid) {
      if (x + width / 2 <= obstacle.x - 6 || x - width / 2 >= obstacle.x + obstacle.w + 6 ||
        y + height / 2 + 12 <= obstacle.y - 6 || y - height / 2 >= obstacle.y + obstacle.h + 6) continue;
      const candidates = [
        { x: obstacle.x - width / 2 - 9, y },
        { x: obstacle.x + obstacle.w + width / 2 + 9, y },
        { x, y: obstacle.y - height / 2 - 21 },
      ].filter(candidate => candidate.x - width / 2 >= 8 && candidate.x + width / 2 <= 1192 && candidate.y - height / 2 >= 8);
      candidates.sort((a, b) => Math.abs(a.x - x) + Math.abs(a.y - y) - Math.abs(b.x - x) - Math.abs(b.y - y));
      if (candidates[0]) ({ x, y } = candidates[0]);
    }
    line(ctx, [x, y + height / 2 - 2, x, y + height / 2 + 12], '#90866b', 3.5);
    rounded(ctx, x - width / 2, y - height / 2, width, height, 4);
    ctx.fillStyle = p.paper;
    ctx.globalAlpha = 0.93;
    ctx.fill();
    ctx.globalAlpha = 1;
    ctx.strokeStyle = '#adab86';
    ctx.lineWidth = 1;
    ctx.stroke();
    disc(ctx, x - width / 2 + 5, y, 1, '#a99a74');
    disc(ctx, x + width / 2 - 5, y, 1, '#a99a74');
    shown.forEach((label, index) => text(ctx, label, x, y + (index - (shown.length - 1) / 2) * 14, 11, '#4a5e4c', 'center', 600));
    ctx.restore();
  }
}
